import React, { useState } from "react";

/**
 * TerminalScreen — a single figure slot for a Data Terminal screenshot.
 *
 * Aspect ratio is computed from the caller's `width` / `height` (required)
 * so Group A landscape shots and Group B portrait shots each get their
 * own honest ratio — no cropping, no blurring, no retouching.
 *
 * Layout rules per PRD:
 * - Container uses `aspect-ratio: {width} / {height}` inline so the box
 *   holds the shape even before the image decodes.
 * - Desktop (≥ 768px) caps the container at `70vh` so a tall portrait
 *   shot cannot dominate the viewport. Mobile stays full-width, ratio
 *   preserved.
 * - `object-fit: contain` guarantees the entire screenshot renders. Any
 *   gutters left behind fill with `--surface-raised`, matching the
 *   pre-load PENDING state — no letterboxing artefact.
 *
 * The PENDING overlay hides on the img's `onLoad`. It stays put on error.
 */
export function TerminalScreen({
  src,
  alt,
  caption,
  width,
  height,
  pendingLabel = "PENDING",
  eager = false,
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <figure
      className="flex flex-col gap-3"
      data-testid={`terminal-screen-${src.split("/").pop()}`}
    >
      <div
        className="relative mx-auto w-full overflow-hidden rounded-[16px] border border-hairline md:max-h-[70vh]"
        style={{
          aspectRatio: `${width} / ${height}`,
          background: "var(--surface-raised)",
          borderColor: "var(--border-hairline)",
        }}
      >
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setLoaded(true)}
          className="absolute inset-0 h-full w-full object-contain"
        />
        {!loaded && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{
              background:
                "radial-gradient(120% 120% at 75% 75%, rgba(114,0,219,0.28) 0%, rgba(42,10,74,0.55) 55%, rgba(2,4,11,0.9) 100%)",
            }}
          >
            <span
              className="font-mono text-xs font-semibold uppercase tracking-caps"
              style={{ color: "var(--text-tertiary)", letterSpacing: "0.14em" }}
            >
              {pendingLabel}
            </span>
          </div>
        )}
      </div>
      <figcaption className="text-sm leading-normal text-ink-secondary">
        {caption}
      </figcaption>
    </figure>
  );
}

export default TerminalScreen;
